import {IconButton, Separator} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';
import styled, {css} from 'styled-components';

import {KeyboardEvent, useRef} from 'react';
import {createPortal} from 'react-dom';

import {isNil, isNotNil} from 'ramda';

import i18n from '@dms/i18n';
import {MAIN_TOPBAR_HEIGHT} from '@dms/shared';

import {CALL_PANEL_WIDTH} from '../../constants/CALL_PANEL_WIDTH';
import {CallPanelMode, useCallPanel} from '../../hooks/useCallPanel';
import {useCallPanelContent} from '../../hooks/useCallPanelContent';
import {usePairableCall} from '../../hooks/usePairableCall';
import {TelephonyCallPanelPairing} from './TelephonyCallPanelPairing';

const FLOATING_OFFSET = 6;
const FLOATING_GUTTER = 12;
const FLOATING_HEIGHT = '70vh';

/**
 * The call panel, opened from the top-bar call chip and kept open across navigation. Docked, it
 * sits under the top bar and the layout narrows the page by its width (`useCallPanelDock`);
 * floating, it lies over the page's bottom-right
 * corner (always on narrow screens). The indicator renders the live call into its top slot and
 * "availability after the call" into its footer slot; pairing, the note and the after-call state
 * belong to the viewer's own call.
 */
export function TelephonyCallPanel() {
  const {state: panel, store: panelStore} = useCallPanel();
  const {store: pairableStore} = usePairableCall();
  const {isShown, isAfterCall, isLivePartHidden, shownPairableCallId} = useCallPanelContent();
  const frameRef = useRef<HTMLDivElement | null>(null);

  if (!isShown) {
    return null;
  }

  const isFloating = panel.mode === 'floating';

  // After the call, closing ends the after-call state like Done: nothing could reopen it later.
  const handleClose = () => {
    const hadFocus = frameRef.current?.contains(document.activeElement) ?? false;

    if (isAfterCall && isNotNil(shownPairableCallId)) {
      pairableStore.dismiss(shownPairableCallId);
    }
    panelStore.close();

    if (hadFocus) {
      panel.focusReturn?.focus();
    }
  };

  const handleFrameRef = (frame: HTMLDivElement | null) => {
    frameRef.current = frame;

    if (isNotNil(frame) && panelStore.getState().isFocusRequested) {
      panelStore.clearFocusRequest();
      frame.focus();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      handleClose();
    }
  };

  return createPortal(
    <>
      <PanelFrame
        ref={handleFrameRef}
        $mode={panel.mode}
        role="complementary"
        aria-label={i18n.t('page.telephony.panel.title')}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        data-testid="telephonyCallPanel"
      >
        <VStack height="100%">
          <Box
            paddingHorizontal={4}
            paddingVertical={3}
            borderBottom="1px solid"
            borderColor="general.separator"
          >
            <HStack align="center" justify="space-between">
              <Text alternative>{i18n.t('page.telephony.panel.title')}</Text>
              <HStack spacing={1}>
                <IconButton
                  icon={isFloating ? 'action/view_column' : 'action/open_in_new'}
                  aria-label={
                    isFloating
                      ? i18n.t('page.telephony.panel.dockToSidebar')
                      : i18n.t('page.telephony.panel.switchToFloating')
                  }
                  onClick={panelStore.toggleMode}
                  data-testid="telephonyCallPanel-mode"
                />
                <IconButton
                  icon="navigation/close"
                  aria-label={i18n.t('general.actions.close')}
                  onClick={handleClose}
                  data-testid="telephonyCallPanel-close"
                />
              </HStack>
            </HStack>
          </Box>
          <Box flex={1} overflowY="auto">
            <div ref={panelStore.setSlot} hidden={isLivePartHidden} />
            <Show when={shownPairableCallId}>
              {(callId) => (
                <>
                  <Show when={!isLivePartHidden}>
                    <Separator spacing={0} />
                  </Show>
                  <TelephonyCallPanelPairing key={callId} callId={callId} onDone={handleClose} />
                </>
              )}
            </Show>
            <Show when={isNil(shownPairableCallId)}>
              <Box padding={4}>
                <Text size="small" color="secondary">
                  {i18n.t('page.telephony.panel.notConnected')}
                </Text>
              </Box>
            </Show>
            <div hidden={isLivePartHidden}>
              <Box paddingHorizontal={4} paddingBottom={4}>
                <div ref={panelStore.setFooterSlot} />
              </Box>
            </div>
          </Box>
        </VStack>
      </PanelFrame>
    </>,
    document.body
  );
}

/** Floating everywhere; docked only where the page has room for it. */
const PanelFrame = styled.div<{$mode: CallPanelMode}>`
  position: fixed;
  /* Clear of a docked assistant sidebar. Its gutter may be a unitless 0, which calc() rejects. */
  right: var(--dms-assistant-gutter, 0);
  transform: translateX(calc(-1 * ${({theme}) => theme.getSize(FLOATING_OFFSET)}));
  bottom: ${({theme}) => theme.getSize(FLOATING_OFFSET)};
  width: ${({theme}) => theme.getSize(CALL_PANEL_WIDTH)};
  max-width: calc(100vw - ${({theme}) => theme.getSize(FLOATING_GUTTER)});
  height: ${FLOATING_HEIGHT};
  overflow: hidden;
  background: ${({theme}) => theme.colors.general.white};
  border: 1px solid ${({theme}) => theme.colors.general.separator};
  border-radius: ${({theme}) => theme.radii.medium};
  box-shadow: ${({theme}) => theme.shadows.elevation_4};
  outline: none;
  /* Under the ringing call bar and every dialog; over the page. */
  z-index: ${({theme}) => theme.zIndices.HEADER};

  ${({$mode, theme}) =>
    $mode === 'docked' &&
    css`
      @media ${theme.devices.notebook}, ${theme.devices.desktop} {
        top: ${theme.getSize(MAIN_TOPBAR_HEIGHT)};
        right: var(--dms-assistant-gutter, 0);
        transform: none;
        bottom: 0;
        height: auto;
        border-width: 1px 0 0 1px;
        border-radius: 0;
        box-shadow: ${theme.shadows.elevation_2};
      }
    `}
`;
